import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
    ITimesheetEmployee, ITimesheetEquipment, ITimesheetRental,
    ITimesheetMaterial, ITimesheetTask, ITaskHours
} from 'shared/models/timesheet.model';

export interface HrDailyTimesheet {
    _id: string;
    projectId: string;
    projectName: string;
    date: string;
    weatherCondition?: string;
    temperature?: string;
    incidentReport?: string;
    notes?: string;
    employees: ITimesheetEmployee[];
    tasks: ITimesheetTask[];
    equipment: ITimesheetEquipment[];
    rentals: ITimesheetRental[];
    materials: ITimesheetMaterial[];
    approvalStatus: string;
    lastModifiedByName?: string;
    lastModifiedDate?: string;
    createdByName?: string;
    createdAt?: string;
}

export interface HrDailyTimesheetDetailsDialogData {
    timesheet: HrDailyTimesheet;
    highlightEmployeeId?: string;
    mode?: 'view' | 'notification-review';
}

@Component({
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, MatTooltipModule],
    selector: 'app-hr-daily-timesheet-details-dialog',
    templateUrl: './hr-daily-timesheet-details-dialog.component.html',
    styleUrls: ['./hr-daily-timesheet-details-dialog.component.scss']
})
export class HrDailyTimesheetDetailsDialogComponent {

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: HrDailyTimesheetDetailsDialogData,
        private dialogRef: MatDialogRef<HrDailyTimesheetDetailsDialogComponent>,
        private _router: Router
    ) {}

    get ts(): HrDailyTimesheet {
        return this.data.timesheet;
    }

    get formattedDate(): string {
        if (!this.ts.date) return '';
        const d = new Date(this.ts.date + 'T00:00:00');
        return d.toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        });
    }

    get activeEmployees(): ITimesheetEmployee[] {
        return this.ts.employees.filter(e => e.employee_id && this.totalHours(e.task_hours) > 0);
    }

    get activeEquipment(): ITimesheetEquipment[] {
        return this.ts.equipment.filter(e => e.equipment_id && this.totalHours(e.task_hours) > 0);
    }

    get activeRentals(): ITimesheetRental[] {
        return this.ts.rentals.filter(r => r.rental_id && this.totalHours(r.task_hours) > 0);
    }

    get activeMaterials(): ITimesheetMaterial[] {
        return this.ts.materials.filter(m => m.material_id && this.totalQuantity(m) > 0);
    }

    get productionTasks(): ITimesheetTask[] {
        return this.ts.tasks.filter(t => t.quantity > 0);
    }

    formatEmployeeName(name: string): string {
        if (!name) return '';
        const parts = name.trim().split(' ');
        if (parts.length < 2) return name;
        const last = parts.pop();
        return `${last}, ${parts[0].charAt(0)}.`;
    }

    getTaskBreakdown(taskHours: Record<string, ITaskHours>): { task: string; label: string }[] {
        const result: { task: string; label: string }[] = [];
        for (const [taskName, hours] of Object.entries(taskHours || {})) {
            const parts: string[] = [];
            if (hours.reg > 0) parts.push(`Reg: ${hours.reg}h`);
            if (hours.extra > 0) parts.push(`OT: ${hours.extra}h`);
            if (hours.double > 0) parts.push(`DT: ${hours.double}h`);
            if (parts.length > 0) {
                result.push({ task: taskName, label: parts.join(' ') });
            }
        }
        return result;
    }

    getEquipmentTaskBreakdown(taskHours: Record<string, ITaskHours>): { task: string; label: string }[] {
        const result: { task: string; label: string }[] = [];
        for (const [taskName, hours] of Object.entries(taskHours || {})) {
            const total = (hours.reg || 0) + (hours.extra || 0) + (hours.double || 0);
            if (total > 0) {
                result.push({ task: taskName, label: `${total}h` });
            }
        }
        return result;
    }

    getMaterialTaskBreakdown(mat: ITimesheetMaterial): { task: string; label: string }[] {
        const result: { task: string; label: string }[] = [];
        for (const [taskName, qty] of Object.entries(mat.task_quantities || {})) {
            if (qty > 0) {
                result.push({ task: taskName, label: `${qty} ${mat.unit}` });
            }
        }
        return result;
    }

    materialDisplayName(mat: ITimesheetMaterial): string {
        let name = mat.name;
        if (mat.selected_subcategories?.length > 0) {
            const additives = mat.selected_subcategories.map(s => s.name).join(', ');
            name += ` (${additives})`;
        }
        return name;
    }

    totalHours(taskHours: Record<string, ITaskHours>): number {
        let total = 0;
        for (const hours of Object.values(taskHours || {})) {
            total += (hours.reg || 0) + (hours.extra || 0) + (hours.double || 0);
        }
        return total;
    }

    totalQuantity(mat: ITimesheetMaterial): number {
        let total = 0;
        for (const qty of Object.values(mat.task_quantities || {})) {
            total += qty || 0;
        }
        return total;
    }

    editTimesheet(): void {
        const from = this.data.mode === 'notification-review' ? 'notification' : 'hr-daily';
        const queryParams: any = { mode: 'edit', from };
        if (this.data.highlightEmployeeId) queryParams.highlightEmployee = this.data.highlightEmployeeId;
        this.dialogRef.close('edit');
        this._router.navigate(['/account/timesheets', this.ts._id], { queryParams });
    }

    approve(): void {
        this.dialogRef.close('approved');
    }

    printTimesheet(): void {
        window.print();
    }

    formatDateTime(dateStr?: string): string {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }

    close(): void {
        this.dialogRef.close(null);
    }
}
